import { ShoppingCart } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { fmtMoney } from '../../../shared/format'
import { useCategories } from '../../api'
import { useAddCardTxn, type CardTxnKind, type CreditCard } from '../../credit'
import { toastError } from '../../ui'
import { Footer } from '../AccountForms'
import { Field, FormSection } from '../FormBits'

const KINDS: Record<'company' | 'personal', CardTxnKind[]> = {
  // Şirket kartında peşin harcama/ödeme hesap hareketinden gelir; burada taksitli harcama, ücret ve iade girilir.
  company: ['purchase', 'fee', 'refund'],
  personal: ['purchase', 'cash_advance', 'fee', 'refund', 'payment'],
}
const INSTALLMENTS = [1, 2, 3, 4, 5, 6, 8, 9, 10, 12, 18, 24, 36]

/** Kart hareketi: taksitli harcama (dönemlere bölünür), nakit avans, ücret, iade ya da (kişisel) ödeme. */
export function CardTxnForm({ card, onDone }: { card: CreditCard; onDone: () => void }) {
  const { t } = useTranslation()
  const add = useAddCardTxn(card.id)
  const company = card.scope === 'company'
  const categories = (useCategories().data ?? []).filter((c) => c.kind === 'expense' && c.active)
  const [kind, setKind] = useState<CardTxnKind>(company ? 'purchase' : 'purchase')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [amount, setAmount] = useState('')
  const [installments, setInstallments] = useState('1')
  const [description, setDescription] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [touched, setTouched] = useState(false)
  const value = Number(amount.replace(',', '.'))
  const n = kind === 'purchase' ? Number(installments) : 1
  const errors = {
    amount: !(value > 0) ? t('finance.form.amountInvalid') : '',
    category: company && kind !== 'fee' && !categoryId ? t('finance.credit.txn.categoryRequired') : '',
  }
  const valid = !errors.amount && !errors.category

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onDone()}
      dismissible={!add.isPending}
      icon={<ShoppingCart />}
      title={t('finance.credit.txn.title', { card: card.name })}
      description={t(company ? 'finance.credit.txn.companyHint' : 'finance.credit.txn.personalHint')}
      onSubmit={(e) => {
        e.preventDefault()
        setTouched(true)
        if (!valid) return
        add.mutate(
          { date, kind, amount: value.toFixed(2), installments: n, description, categoryId },
          {
            onSuccess: () => {
              toast.success(t('finance.credit.txn.saved'))
              onDone()
            },
            onError: toastError,
          },
        )
      }}
      footer={<Footer pending={add.isPending} onCancel={onDone} />}
    >
      <FormSection>
        <Field id="ct-kind" label={t('finance.credit.txn.kind')}>
          <Select
            id="ct-kind"
            value={kind}
            onChange={(k) => setKind(k as CardTxnKind)}
            options={KINDS[card.scope].map((k) => ({ value: k, label: t(`finance.credit.kind.${k}`) }))}
          />
        </Field>
        <Field id="ct-date" label={t('finance.tx.date')}>
          <DateField id="ct-date" value={date} onChange={setDate} clearable={false} />
        </Field>
        <Field id="ct-amount" label={t('finance.amount')} error={touched ? errors.amount || undefined : undefined}>
          <Input id="ct-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        {kind === 'purchase' ? (
          <Field
            id="ct-inst"
            label={t('finance.credit.txn.installments')}
            hint={
              n > 1 && value > 0
                ? t('finance.credit.txn.perInstallment', { amount: fmtMoney((value / n).toFixed(2)) })
                : undefined
            }
          >
            <Select
              id="ct-inst"
              value={installments}
              onChange={setInstallments}
              options={INSTALLMENTS.map((i) => ({
                value: String(i),
                label: i === 1 ? t('finance.credit.txn.single') : t('finance.credit.txn.nInstallments', { count: i }),
              }))}
            />
          </Field>
        ) : null}
        {company && kind !== 'fee' ? (
          <Field id="ct-cat" label={t('finance.category')} error={touched ? errors.category || undefined : undefined}>
            <Select
              id="ct-cat"
              value={categoryId}
              onChange={setCategoryId}
              placeholder={t('common.select')}
              searchable
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Field>
        ) : null}
        <Field id="ct-desc" label={t('finance.credit.txn.description')} full>
          <Input id="ct-desc" maxLength={200} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </FormSection>
    </Modal>
  )
}
